import { usePostNavigation } from '@/features/(insight)/posts/hooks/use-post-navigation';
import { Check, FolderOpen } from 'lucide-react';
import { PostContent } from './post-content';
import { PostFooter } from './post-footer';
import { PostMedia } from './post-media';
import { PostStats } from './post-stats';

import type { PostListItem } from '@/entities/post/types';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/constants';
import { showProfilePopup } from '@/features/(insight)/shared/profile-popup/index';
import { Button } from '@/shared/components/shadcn/button';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { cn } from '@/shared/utils/cn';
import { type ReactNode, useMemo } from 'react';
import { CreatorProfile } from '@/features/creator/ui/creator-profile/creator-profile';

interface SocialPostCardProps<TPost extends PostListItem = PostListItem> {
  contentTopSlot?: ReactNode;
  footerLabel?: string;
  footerUpdatedAt?: DateISOString;
  post: TPost;
  isSelected?: boolean;
  onToggleSelect?: (post: TPost) => void;
  savedBoardCount?: number;
  onRemove?: (postId: string) => void;
  removeTooltip?: string;
}

type PostListItemWithLabel = PostListItem & {
  label?: string;
};

function hasPostLabel(post: PostListItem): post is PostListItemWithLabel {
  return 'label' in post;
}

export function SocialPostCard<TPost extends PostListItem = PostListItem>({
  contentTopSlot,
  footerLabel,
  footerUpdatedAt,
  post,
  isSelected = false,
  onToggleSelect,
  savedBoardCount = 0,
  onRemove,
  removeTooltip,
}: SocialPostCardProps<TPost>) {
  const toPostPage = usePostNavigation();

  const handleProfileClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const { author } = post;
    await showProfilePopup({
      position: { x: e.clientX, y: e.clientY },
      profileImageUrl: author.profile_image_url,
      username: author.username,
      nickname: author.nickname,
      followerCount: author.follower_count,
      profileUrl: author.profile_url,
    });
  };

  const postedAt = post.date;

  const displayingContent = useMemo(() => {
    return post.extraction.title || post.extraction.content || '';
  }, [post.extraction.content, post.extraction.title]);
  const postLabel = hasPostLabel(post) ? post.label : undefined;

  return (
    <div
      className={cn(
        'group relative flex h-full flex-col rounded-lg shadow-sm transition-all duration-300',
        isSelected && onToggleSelect
          ? 'border-primary/50 bg-primary/5 ring-primary/30 hover:border-primary border ring-1 hover:-translate-y-1 hover:shadow-md'
          : 'border border-gray-200 bg-zinc-100 hover:-translate-y-1 hover:border-gray-400 hover:shadow-lg',
      )}
    >
      {onRemove ? (
        <TooltipFlat asChild side="bottom" content={removeTooltip ?? 'Remove from board'}>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="absolute top-2 right-2 h-9 px-4 text-sm font-medium opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-zinc-900/10"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(post._id);
            }}
            aria-label="Remove from board"
          >
            Remove
          </Button>
        </TooltipFlat>
      ) : onToggleSelect ? (
        <div className="absolute top-2 right-2">
          <Button
            variant={isSelected ? 'default' : 'secondary'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect(post);
            }}
            aria-label={isSelected ? 'Deselect post' : 'Select post'}
            aria-pressed={isSelected}
            size="sm"
            className={cn(
              'h-9 w-18',
              isSelected ? 'w-9' : 'opacity-0 group-hover:opacity-100 hover:bg-zinc-900/10',
            )}
          >
            {isSelected ? <Check className="size-4" /> : null}
            {!isSelected && <span>Select</span>}
          </Button>
        </div>
      ) : null}
      {savedBoardCount > 0 && (
        <TooltipFlat
          asChild
          side="bottom"
          content={`Saved in ${savedBoardCount} board${savedBoardCount > 1 ? 's' : ''}`}
        >
          <div className="absolute -top-2 -left-2 inline-flex h-7 w-7 items-center justify-center rounded-full border border-zinc-200 bg-zinc-100 shadow-sm">
            <FolderOpen className="h-3.5 w-3.5 text-zinc-600" />
          </div>
        </TooltipFlat>
      )}
      <div className="flex h-full cursor-pointer flex-col" onClick={() => toPostPage(post._id)}>
        <CreatorProfile
          username={post.author.username}
          displayName={post.author.nickname || post.author.username}
          avatar={post.author.profile_image_url}
          postedAt={postedAt}
          onProfileClick={handleProfileClick}
          platform={post.source.platform}
        />
        <div className="space-y-4 px-4 pb-4">
          {(() => {
            const hasVideo = post.extraction.video.url != null;
            if (hasVideo) {
              return (
                <PostMedia
                  type="VIDEO"
                  isSponsored={post.extraction.sponsored}
                  videoSource={post.extraction.video.url!}
                  posterUrl={post.extraction.video.poster_url}
                  label={postLabel}
                />
              );
            } else {
              return (
                <PostMedia
                  type="CAROUSEL"
                  isSponsored={post.extraction.sponsored}
                  posterUrl={post.extraction.video.poster_url}
                  label={postLabel}
                />
              );
            }
          })()}
          {contentTopSlot ? (
            <div className="-mt-1 flex items-start pb-1">{contentTopSlot}</div>
          ) : null}
          <PostContent content={displayingContent} />
          <PostStats
            views={post.extraction.engagement.view}
            likes={post.extraction.engagement.like}
            comments={post.extraction.engagement.comment}
            shares={post.extraction.engagement.shared}
            engagementRate={Number((post.extraction.engagement.rate * 100).toFixed(1))}
            hideShares={post.source.platform !== SOURCE_PLATFORM_TYPE.TIKTOK}
          />
        </div>
      </div>
      <PostFooter label={footerLabel} updatedAt={footerUpdatedAt ?? post.date} />
    </div>
  );
}
